<template>
    <div class="crud-wrapper">
        <primevue-select
            :model-value="props.modelValue"
            :input-id="props.inputId"
            option-label="name"
            option-value="id"
            :options="optionsAsIdentifiers"
            :placeholder="selectionPlaceholder"
            @update:model-value="updateSelection"
        />
        <popover ref="crudOverlay" close-on-escape>
            <div v-if="selectedCrudMode" class="crud-overlay">
                <h4 class="header">{{ props.headersByMode[selectedCrudMode] }}</h4>
                <div v-if="entryIsInvalid && validationMessage" class="validation-message">
                    {{ validationMessage }}
                </div>
                <template v-if="selectedCrudMode !== 'delete'">
                    <label :for="`input-${selectedCrudMode}`">New {{ props.name }} name</label>
                    <input-text v-model="newTextEntry" :class="{ invalid: entryIsInvalid }" :id="`input-${selectedCrudMode}`" :invalid="entryIsInvalid" @keydown.enter="confirm" />
                </template>
                <div class="button-row">
                    <p-button class="plain-btn" @click="closeOverlay">Cancel</p-button>
                    <p-button :class="{ delete: selectedCrudMode === 'delete' }" @click="confirm" :disabled="isCreateDisabled">
                        {{ buttonText }}
                    </p-button>
                </div>
            </div>
        </popover>
        <p-button
            v-tooltip="`Create new ${props.name}`"
            :aria-label="`Create new ${props.name}`"
            text
            @click="openOverlay($event, 'create')"
        >
            <font-awesome-icon :icon="['fas', 'plus']" />
        </p-button>
        <p-button
            v-tooltip="`Edit ${props.name}`"
            :aria-label="`Edit ${props.name}`"
            text
            @click="openOverlay($event, 'update')"
        >
            <font-awesome-icon :icon="['fas', 'pencil']" />
        </p-button>
        <p-button
            v-tooltip="`Delete ${props.name}`"
            :aria-label="`Delete ${props.name}`"
            class="delete"
            text
            :disabled="!props.nullable && options.length === 1"
            @click="openOverlay($event, 'delete')"
        >
            <font-awesome-icon :icon="['fas', 'trash-can']" />
        </p-button>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { TCrudMode } from '@/types/general/TCrudMode.ts';
import PButton from 'primevue/button';
import Popover from 'primevue/popover';
import PrimevueSelect from 'primevue/select';
import InputText from 'primevue/inputtext';

const props = withDefaults(defineProps<{
    modelValue: string | null;
    options: Array<string> | Array<IIdName>;
    headersByMode: Record<TCrudMode, string>;
    name: string;
    nullable?: boolean;
    selectionPlaceholder?: string;
    inputId?: string;
}>(), {
    headersByMode: () => ({
        create: 'Create',
        update: 'Update',
        delete: 'Delete',
    }),
    selectionPlaceholder: 'Select group',
    inputId: undefined,
});

const emit = defineEmits<{
    'update:selection': [string];
    'create': [string];
    'update': [string];
    'delete': [];
}>();

const selectedCrudMode = ref<TCrudMode | null>(null);
const crudOverlay = ref<InstanceType<typeof Popover> | null>(null);
const validationMessage = ref<string>('');
const entryIsInvalid = ref<boolean>(false);
const newTextEntry = ref<string>('');

const isCreateDisabled = computed(() => selectedCrudMode.value !== 'delete' && !newTextEntry.value);

const optionsAsIdentifiers = computed<Array<IIdName>>(() => {
    let options: Array<IIdName>;

    if (typeof props.options[0] === 'string') {
        options = props.options.map((option) => ({ id: option, name: option })) as Array<IIdName>;
    } else {
        options = props.options as Array<IIdName>;
    }

    return options;
});

const buttonText = computed<string>(() => (selectedCrudMode.value ? selectedCrudMode.value[0].toUpperCase() + selectedCrudMode.value.slice(1).toLowerCase() : ''));

function openOverlay (event: MouseEvent, crudMode: TCrudMode): void {
    if (!crudOverlay.value) return;
    selectedCrudMode.value = crudMode;
    entryIsInvalid.value = false;
    newTextEntry.value = crudMode === 'update' ? optionsAsIdentifiers.value.find(o => o.id === props.modelValue)?.name ?? '' : '';
    crudOverlay.value.show(event);
    crudOverlay.value.alignOverlay();
}

function closeOverlay (): void {
    if (crudOverlay.value) crudOverlay.value.hide();
    selectedCrudMode.value = null;
}

function validateInput (input: string, crudMode: TCrudMode): void {
    if (crudMode === 'delete') {
        entryIsInvalid.value = false;
        return;
    }

    if (['create', 'update'].includes(crudMode)) {
        if (optionsAsIdentifiers.value.some(o => o.name.includes(input))) {
            entryIsInvalid.value = true;
            validationMessage.value = `Item with name ${input} already exists`;
        } else if (!input) {
            entryIsInvalid.value = true;
            validationMessage.value = 'Item name cannot be empty';
        } else {
            entryIsInvalid.value = false;
        }
    }
}

function confirm (): void {
    if (!selectedCrudMode.value) return;

    validateInput(newTextEntry.value, selectedCrudMode.value);

    if (entryIsInvalid.value) return;

    switch (selectedCrudMode.value) {
        case 'create': {
            emit('create', newTextEntry.value);
            break;
        }
        case 'update': {
            emit('update', newTextEntry.value);
            break;
        }
        case 'delete': {
            emit('delete');
            break;
        }
    }

    selectedCrudMode.value = null;
    if (crudOverlay.value) crudOverlay.value.hide();
}

function updateSelection (id: string): void {
    emit('update:selection', id);
}

</script>

<style lang="scss" scoped>

.crud-wrapper {
    display: flex;
    gap: var(--defaultSpacing);
    align-items: center;

    :deep(.p-inputtext) {
        width: 250px;
    }

    .delete {
        color: var(--lcpWatermelon) !important;
    }
}

.crud-overlay {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
    padding: var(--defaultSpacing);

    .button-row {
        padding-top: var(--defaultSpacing);
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        .delete {
            background-color: var(--lcpWatermelon) !important;
        }
    }

    .validation-message {
        color: var(--dangerRed);
        font-size: var(--smallFontSize);
        text-align: right;
    }

    .confirm-message {
        min-width: 150px;
        padding: var(--smallSpacing) var(--defaultSpacing);

        h5 {
            margin-bottom: var(--smallSpacing);
        }
    }
}

</style>
